import axios from 'axios';

// Helper to read a cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Base URL of your Django REST API
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
// Create an Axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // Optional: Set a timeout for requests (in milliseconds)
  withCredentials: true, // send cookies with requests
});

// Separate instance without auth headers for public endpoints
const publicApi = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

// Add an interceptor to attach the Authorization header to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || getCookie('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`; // Use "Token" prefix as required by Django
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error); // Log request errors for debugging
    return Promise.reject(error);
  }
);

// Handle API responses and errors
api.interceptors.response.use(
  (response) => {
    return response; // Pass successful responses as-is
  },
  (error) => {
    // Handle specific error cases (e.g., token expiration, 403 errors)
    if (error.response?.status === 401) {
      console.error('Unauthorized: Please log in again.'); // Log unauthorized errors
    } else if (error.response?.status === 403) {
      console.error('Forbidden: You do not have permission to access this resource.'); // Log forbidden errors
    } else {
      console.error('API error:', error.response?.data || error.message); // Log other API errors
    }
    return Promise.reject(error);
  }
);

// Utility function to dynamically set or clear Authorization token
const setAuthToken = (token) => {
  if (token) {
    // Attach token to the common headers so it is sent with all requests
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
    console.log('Auth token set globally:', token); // Debug log
  } else {
    delete api.defaults.headers.common['Authorization'];
    console.log('Auth token cleared globally'); // Debug log
  }
};

// API methods for specific resources

export const fetchInstitutions = async () => {
  // Request the full list of institutions including unapproved ones
  const response = await publicApi.get('/courses/institutions/?show_all=true');
  return response.data;
};

// Fetch departments
export const fetchDepartments = async (institutionId) => {
  try {
    const params = institutionId ? { institution_id: institutionId } : {};
    const response = await api.get('/courses/departments/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching departments:', error.response?.data || error.message);
    throw error;
  }
};

// Fetch all courses
export const fetchCourses = async (institutionId, params = {}) => {
  const query = { ...params };
  if (institutionId) query.institution_id = institutionId;
  const response = await api.get('/courses/courses/', { params: query });
  return response.data;
};

// Fetch a specific course by ID
export const fetchCourseDetails = async (id) => {
  const response = await api.get(`/courses/courses/${id}/`);
  return response.data;
};

export const fetchProfessorDetails = async (id) => {
  const response = await api.get(`/courses/professors/${id}/`);
  return response.data;
};

export const fetchProfessorSubmissionDetails = async (id) => {
  const response = await api.get(`/courses/professor-submissions/${id}/`);
  return response.data;
};

// Add a new review
export const addReview = async (reviewData) => {
  const response = await api.post('/courses/reviews/', reviewData);
  return response.data;
};

// Fetch all professors
export const fetchProfessors = async () => {
  const response = await api.get('/courses/professors/');
  return response.data;
};

// Fetch all semesters
export const fetchSemesters = async () => {
  const response = await api.get('/courses/semesters/');
  return response.data;
};

// Fetch professors by course
export const fetchProfessorsByCourse = async (courseId) => {
  const response = await api.get(`/courses/courses/${courseId}/professors/`);
  return response.data;
};

export const fetchCoursesByProfessor = async (professorId) =>
  api.get(`/courses/professors/${professorId}/courses/`);

// Fetch reviews for a specific course and professor, with optional query parameters
export const fetchReviews = async (courseId, professorId, params = {}) => {
  // Convert params to a query string
  const queryString = new URLSearchParams(params).toString();
  const url = `/courses/reviews/${courseId}/${professorId}/?${queryString}`;

  // console.log('API request URL:', url); // Debug log to confirm query string
  const response = await api.get(url);

  // console.log('Backend response:', response.data); // Debug log to verify backend response
  return response.data;
};

// Fetch reviews created by the currently authenticated user
export const fetchMyReviews = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/courses/reviews/mine/', { params });
  // The backend exposes moderation fields so the caller can
  // display removal reasons and decision timestamps.
  return response.data;
};

// Fetch courses submitted by the current user
// Each course object includes `resubmission_count` and `previous_submission`
// when the course has been resubmitted.
export const fetchMyCourses = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/courses/my-submissions/', { params });
  return response.data;
};

// Fetch professors submitted by the current user
// The response mirrors `fetchMyCourses`, exposing `resubmission_count`
// and `previous_submission` for resubmitted entries.
export const fetchMyProfessors = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/courses/my-professors/', { params });
  return response.data;
};

export const fetchMyProfessorCourses = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/courses/my-professor-courses/', { params });
  return response.data;
};

export const fetchMyProfessorSubmissions = async () => {
  const response = await api.get('/courses/my-professor-submissions/');
  return response.data;
};

export const createProfessorSubmission = async (data) => {
  const response = await api.post('/courses/professor-submissions/', data);
  return response.data;
};

export const updateProfessorSubmission = async (id, data) => {
  const response = await api.patch(`/courses/professor-submissions/${id}/`, data);
  return response.data;
};

// Fetch departments submitted by the current user
export const fetchMyDepartments = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/courses/my-departments/', { params });
  return response.data;
};

// Update a department (resubmit)
export const updateDepartment = async (id, data) => {
  const response = await api.patch(`/courses/departments/${id}/`, data);
  return response.data;
};

// Delete a department by ID
export const deleteDepartment = async (id) => {
  const response = await api.delete(`/courses/departments/${id}/`);
  return response.data;
};

// Update a course (resubmit)
export const updateCourse = async (id, data) => {
  const response = await api.patch(`/courses/courses/${id}/`, data);
  return response.data;
};

// Update a professor (resubmit)
export const updateProfessor = async (id, data) => {
  const response = await api.patch(`/courses/professors/${id}/`, data);
  return response.data;
};

export const updateProfessorCourse = async (id, data) => {
  const response = await api.patch(`/courses/professor-courses/${id}/`, data);
  return response.data;
};

// Delete a course by ID
export const deleteCourse = async (id) => {
  const response = await api.delete(`/courses/courses/${id}/`);
  return response.data;
};

// Delete a professor by ID
export const deleteProfessor = async (id) => {
  const response = await api.delete(`/courses/professors/${id}/`);
  return response.data;
};

// Update an existing review
export const updateReview = async (id, data) => {
  const response = await api.patch(`/courses/reviews/${id}/`, data);
  return response.data;
};

// Delete a review by ID
export const deleteReview = async (id) => {
  const response = await api.delete(`/courses/reviews/${id}/`);
  return response.data;
};


// Register a new user (Sign Up)
export const registerUser = async (userData) => {
  try {
    const response = await publicApi.post('/users/signup/', userData);
    return response.data;
  } catch (error) {
    console.error('Error during registration:', error.response?.data || error.message); // Debug log
    throw new Error(error.response?.data?.error || 'Registration failed.');
  }
};

// Login user
export const loginUser = async (credentials) => {
  try {
    const response = await api.post('/users/login/', credentials);
    const { token, user, email_verified } = response.data; // Extract token and user details
    localStorage.setItem('token', token); // Save token in localStorage
    setAuthToken(token); // Set Authorization header for future requests
    console.log('Login successful. Token and user details saved.'); // Debug log
    return { token, user, email_verified };
  } catch (error) {
    console.error('Login failed:', error.response?.data || error.message); // Debug log
    throw new Error(error.response?.data?.error || 'Login failed.');
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await api.post('/users/logout/');
  } catch (e) {
    console.error('Logout request failed:', e.response?.data || e.message);
  }
  localStorage.removeItem('token'); // Remove token from localStorage
  localStorage.removeItem('institutionId'); // Clear institution selection
  setAuthToken(null); // Clear Authorization header
  console.log('User logged out. Token removed.'); // Debug log
};

export const changePassword = async (oldPassword, newPassword) => {
  const res = await api.post('/users/change-password/', {
    old_password: oldPassword,
    new_password: newPassword,
  });
  return res.data;
};

export const changeEmail = async (email) => {
  const res = await api.post('/users/change-email/', { email });
  return res.data;
};

export const requestPasswordReset = async (email) => {
  // POST to send a password reset email without auth headers
  const res = await publicApi.post('/users/password-reset/', { email });
  return res.data;
};

export const fetchPasswordResetEmail = async (uid, token) => {
  const url = `/users/password-reset-confirm/${uid}/${token}/`;
  const res = await publicApi.get(url);
  return res.data;
};

// Validate a password reset token and return the associated email
export const validateResetToken = async (uid, token) => {
  const url = `/users/password-reset-confirm/${uid}/${token}/`;
  const res = await publicApi.get(url);
  return res.data.email;
};

export const confirmPasswordReset = async (uid, token, newPassword) => {
  const url = `/users/password-reset-confirm/${uid}/${token}/`;
  const res = await publicApi.post(url, {
    new_password1: newPassword,
    new_password2: newPassword,
  });
  return res.data;
};

export const verifyEmail = async (uidb64, token) => {
  // Use the public API instance to avoid sending auth headers
  const res = await publicApi.get('/users/verify-email/', {
    params: { uidb64, token },
  });
  return res.data;
};

export const resendVerificationEmail = async () => {
  const res = await api.post('/users/verify-email/resend/');
  return res.data;
};

export const checkEmailExists = async (email) => {
  const res = await publicApi.get('/users/email-exists/', { params: { email } });
  return res.data.exists;
};

// Fetch all topics
export const fetchTopics = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/community/topics/', { params });
  return response.data;
};

export const fetchTopContributors = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get('/community/top-contributors/', { params });
  return response.data;
};

export const fetchTopTutors = async (institutionId, limit) => {
  const params = {};
  if (institutionId) params.institution_id = institutionId;
  if (limit) params.limit = limit;
  const response = await api.get('/tutoring/top-tutors/', { params });
  return response.data;
};

// Fetch threads created by a specific user
export const fetchUserThreads = async (username, institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const encoded = encodeURIComponent(username);
  const response = await api.get(`/community/users/${encoded}/threads/`, { params });
  return response.data;
};

// Fetch comments created by a specific user
export const fetchUserComments = async (username, institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const encoded = encodeURIComponent(username);
  const response = await api.get(`/community/users/${encoded}/comments/`, { params });
  return response.data;
};

// Fetch profile info for a specific user
export const fetchUserProfileInfo = async (username, institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const encoded = encodeURIComponent(username);
  const response = await api.get(`/community/users/${encoded}/info/`, { params });
  return response.data;
};

export const markThreadSolved = async (threadId) => {
  try {
    const response = await api.post(`/community/threads/${threadId}/mark_solved/`);
    return response.data;
  } catch (error) {
    console.error('Error marking thread solved:', error.response?.data || error.message);
    throw error;
  }
};

// Fetch threads for a specific topic
export const fetchThreadsByTopic = async (topicId) => {
  const response = await api.get(`/community/topics/${topicId}/threads/`);
  return response.data;
};

// Fetch threads for a specific subtopic
export const fetchThreadsBySubtopic = async (subtopicId, institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const response = await api.get(`/community/subtopics/${subtopicId}/threads/`, { params });
  return response.data;
};

// Create a new thread
export const createThread = async (threadData) => {
  const response = await api.post('/community/threads/', threadData);
  return response.data;
};

export const updateThread = (id, data) =>
  api.patch(`/community/threads/${id}/`, data);

export const upvoteThread = (threadId) =>
  api.post(`/community/threads/${threadId}/upvote/`);

export const downvoteThread = (threadId) =>
  api.post(`/community/threads/${threadId}/downvote/`);

// Fetch a single thread by ID
export const fetchThread = async (threadId) => {
  const response = await api.get(`/community/threads/${threadId}/`);
  const data = response.data;
  const updatedComments = data.comments.map(comment => ({
    ...comment,
    user: {
      id: comment.created_by?.id,
      name: comment.created_by?.display_name || comment.created_by?.username,
      avatar: comment.created_by?.avatar || '',
    },
  }));

  return {
    ...data,
    thread: {
      ...data.thread,
      view_count: data.thread.view_count,
    },
    comments: updatedComments,
  };
};


export const addComment = async (threadId, content, parentId = null) => {
  try {
    const response = await api.post(`/community/threads/${threadId}/add_comment/`, {
      content,
      parent_id: parentId,
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting comment:", error.response?.data || error.message);
    throw error;
  }
};

export const updateComment = (id, data) =>
  api.patch(`/community/comments/${id}/`, data);

export const upvoteComment = (commentId) =>
  api.post(`/community/comments/${commentId}/upvote/`);

export const downvoteComment = (commentId) =>
  api.post(`/community/comments/${commentId}/downvote/`);

export const deleteThread = (id) =>
  api.delete(`/community/threads/${id}/`);

export const deleteComment = (id) =>
  api.delete(`/community/comments/${id}/`);

export const removeThread = (id, reason) =>
  api.post(`/community/threads/${id}/remove/`, { reason });

export const removeComment = (id, reason) =>
  api.post(`/community/comments/${id}/remove/`, { reason });

export const lockThread = (id, reason) =>
  api.post(`/community/threads/${id}/lock/`, reason ? { reason } : {});

export const lockComment = (id, reason) =>
  api.post(`/community/comments/${id}/lock/`, reason ? { reason } : {});

export const reportThread = (id, reason) =>
  api.post(`/community/threads/${id}/report/`, { reason });

export const reportComment = (id, reason) =>
  api.post(`/community/comments/${id}/report/`, { reason });

export const fetchMyRemovedPosts = async () => {
  const response = await api.get('/community/my-removed-posts/');
  return response.data;
};

export const fetchPostModeration = async (institutionId, params = {}) => {
  const query = { ...params };
  if (institutionId) query.institution_id = institutionId;
  const response = await api.get('/community/post-moderation/', { params: query });
  return response.data;
};

export const dismissPostReport = (id) =>
  api.post(`/community/posts/${id}/dismiss-report/`);

// Fetch all subjects
export const fetchSubjects = async () => {
  const response = await api.get('/tutoring/subjects/');
  return response.data;
};

export const fetchTutors = async (institutionId, filters = {}) => {
  const params = institutionId
    ? { institution_id: institutionId, ...filters }
    : { ...filters };
  const response = await api.get('/tutoring/tutors/', { params });
  return response.data;
};

export const fetchTutorProfile = async (id) => {
  const response = await api.get(`/tutoring/tutors/${id}/`);
  return response.data;
};

export const fetchTopicProposals = async (institutionId, params = {}) => {
  const query = { state: 'PENDING', ...params };
  if (institutionId) query.institution_id = institutionId;
  const response = await api.get('/community/topic-proposals/', { params: query });
  return response.data;
};

export const submitTopicProposal = async (data) => {
  const response = await api.post('/community/topic-proposals/', data);
  return response.data;
};

export const voteTopicProposal = async (id, action, reason = '') => {
  const response = await api.post(`/community/topic-proposals/${id}/vote/`, {
    action,
    reason,
  });
  return response.data;
};

export const createTutoringSession = async (sessionData) => {
  const response = await api.post('/tutoring/sessions/create/', sessionData);
  return response.data;
};

export const fetchCourseRate = async (rateId) => {
  const response = await api.get(`/tutoring/rates/${rateId}/`);
  return response.data;
};

export const fetchMyBookings = async () => {
  const response = await api.get('/tutoring/my-bookings/');
  return response.data;
};

export const fetchMyTutors = async () => {
  const response = await api.get('/tutoring/my-tutors/');
  return response.data;
};

export const fetchInstitutionStats = (id) =>
  api
    .get('/stats/institution/', { params: { institution_id: id } })
    .then((r) => r.data);

export const fetchInstitutionTrendStats = (id) =>
  api
    .get('/stats/trend/', { params: { institution_id: id } })
    .then((r) => r.data);

export const fetchAdminProgress = () =>
  api.get('/users/admin/progress/').then((r) => r.data);

export const updateAdminProgress = (data) =>
  api.patch('/users/admin/progress/', data).then((r) => r.data);

export const applyForAdmin = (app) =>
  api.post('/users/admin/apply/', { app }).then((r) => r.data);

export const fetchInstitutionAdminStats = (id) =>
  api
    .get('/stats/admins/', { params: { institution_id: id } })
    .then((r) => r.data);

export const submitFeedback = async (data) => {
  const res = await api.post('/feedback/feedbacks/', data);
  return res.data;
};

export const fetchMyFeedbacks = async () => {
  const res = await api.get('/feedback/feedbacks/');
  return res.data;
};

export const uploadProfileAvatar = async (file) => {
  const formData = new FormData();
  // Provide a filename so DRF can determine the extension
  formData.append('avatar', file, 'avatar.png');
  const res = await api.put('/users/profile/', formData);
  return res.data;
};

export const uploadCommunityAvatar = async (file) => {
  const formData = new FormData();
  // Provide a filename so DRF can determine the extension
  formData.append('community_avatar', file, 'community_avatar.png');
  const res = await api.put('/users/profile/', formData);
  return res.data;
};

export const deleteCommunityAvatar = async () => {
  const formData = new FormData();
  formData.append('community_avatar', '');
  const res = await api.put('/users/profile/', formData);
  return res.data;
};

export const deleteAvatar = async () => {
  const formData = new FormData();
  formData.append('avatar', '');
  const res = await api.put('/users/profile/', formData);
  return res.data;
};


export default api;
export { publicApi };
